import React, { useState } from 'react';
import { JSONSchemaType } from 'ajv';
import { Tab, useShowController, useTranslate, usePermissions } from 'react-admin';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { time } from '~/utils';
import cx from 'classnames';
import ajv from '~/utils/ajv';
import { buildName } from '~/utils';
import { Show, PaperTabbedShowLayout } from '~/layout';
import { EditAccountInfo, Aside, ApiIdentityTab } from './components';
import {
  Accounts,
  TextField,
  NoPermissions,
  CheckPermissions,
  ShowController,
  Cards,
  ExternalSources,
} from '~/components/ra';
import ActionReportsTab from '~/components/ActionReportsTab';
import {
  TransactionsHistory,
  H2,
  TextField as TextFieldComponent,
  PhoneField,
  DocumentCard,
  Notes,
  AuditLogWidget,
  UpdateContext,
  AddressTable,
  RecordTitle,
  CountryField,
  ProfileTags,
  ApiIdentityAside,
} from '~/components';
import { EditAddress } from '~/components';
import { EditContacts } from '~/components';
import { Individual } from '~/types/individual';
import BeneficiariesList from '../beneficiaries/BeneficiariesList';
import { useConfiguration, useDeepEffect, useSolution } from '~/hooks';
import { useChangelogRecord } from '~/hooks/useChangelogContext';
import { Alert, Skeleton } from '@material-ui/lab';
import JsonSchemaView from '~/components/JsonSchemaView';
import EditAdditionalProperties from './components/EditAdditionalProperties';
import AddOrEditDocument from './components/AddOrEditDocument';
import ProfileLimits from '~/components/ProfileLimits';
import { useRightContentSpacing } from '~/hooks/styles';
import ExternalProfileLinks from '~/components/ExternalProfileLinks';
import EventLogWidget from '~/components/Events/EventLogWidget';
import EventLogKYC from '~/components/Events/EventLogKYC';
import AffiliatedObjects from '~/components/AffiliatedObjects';
import Metrics from './components/Metrics';
import BranchField from '~/components/ra/BranchField';
import Applications from '~/components/Applications';
import LegalConsentsTab from './components/LegalConsentsTab';
import { useEditMode } from '~/hooks/useEditModeContext';
import { removeDraftPostfix } from '~/hooks/draft';
import { DiffHighlight } from '~/components';
import { DiffScope, DraftDiffProvider } from '~/hooks/useDraftDiffContext';

const label = (key: string): string => `resources.individuals.fields.${key}`;

const Layout = (props: any) => {
  const { history, resource, record, ...rest } = props;
  const currentPathname = history?.location.pathname;
  const eventLogPathname = `/${resource}/${record?.id || ''}/show/eventLog`;
  const systemLogPathname = `/${resource}/${record?.id || ''}/show/systemLog`;

  const { permissions } = usePermissions();

  return (
    <PaperTabbedShowLayout
      {...rest}
      history={history}
      resource={resource}
      record={record}
      bottomContent={
        currentPathname === eventLogPathname && permissions?.includes?.('individual.event.list') ? (
          <EventLogWidget objectId={record.id} objectType='individuals' history={history} />
        ) : /*currentPathname === apiIdentityPathname && apiIdentity?.id ? (
          <AuditLogWidget identityId={apiIdentity.id} tags={['s.apiIdentity']} history={history} />
        ) : */ currentPathname ===
          systemLogPathname ? (
          <AuditLogWidget profileId={record.id} history={history} />
        ) : null
      }
    />
  );
};

interface ProfileSchema {
  schema?: JSONSchemaType<Record<string, any>>;
  error?: boolean;
}

const IndividualShow = (props: any) => {
  const { resource, basePath, history } = props;

  const showController = useShowController(props);
  const { editMode } = useEditMode();
  const currentPathname = history?.location.pathname;

  const { permissions } = usePermissions();

  const translate = useTranslate();
  const translateLabel = (key: string): string => translate(label(key));

  const showRecord: Individual | undefined = useChangelogRecord(showController.record);

  const accountsPathname = `/${resource}/${showRecord?.id || ''}/show`;
  const profilePathname = `/${resource}/${showRecord?.id || ''}/show/profile`;
  const transactionsPathname = `/${resource}/${showRecord?.id || ''}/show/transactions`;
  const apiIdentityPathname = `/${resource}/${showRecord?.id || ''}/show/apiIdentity`;
  const isProfileTab = currentPathname === profilePathname;

  const spacing = useRightContentSpacing();
  const classes = useStyles({ spacing });

  const { data: solution } = useSolution(resource.solutionId);

  const [profileSchema, setProfileSchema] = useState<ProfileSchema>();
  const { data: configuration } = useConfiguration(showRecord?.solutionId);
  useDeepEffect(() => {
    if (configuration) {
      const schemaRaw: string | undefined = configuration?.veengu_individual_profile?.profileSchema;
      if (schemaRaw) {
        try {
          const anySchema = JSON.parse(schemaRaw) as any;
          if (anySchema.type !== 'object') {
            throw new Error('Json schema type !== object');
          }
          const schema = anySchema as JSONSchemaType<Record<string, any>>;
          ajv.compile(schema);
          console.log(`using individual profile schema: `, schema);
          setProfileSchema({ schema });
        } catch (e) {
          console.error(`failed to parse individual profile schema`, e);
          setProfileSchema({ error: true });
        }
      } else {
        console.error('no individual profile schema provided');
        setProfileSchema({ error: true });
      }
    }
  }, [configuration]);

  // Prevent undefined check in child component.
  if (!showRecord) return null;
  if (!permissions) return null;
  if (!permissions.includes('individual.view')) return <NoPermissions />;

  const hasBeneficiarySupport =
    solution?.features?.includes('veengu_individual_beneficiary_management') &&
    permissions?.includes('beneficiary.list');

  const hasCardIssuingSupport = solution?.features?.includes('veengu_individual_card_issuing');

  const hasExternalProfileLinksPermission = permissions?.includes('externalProfileLink.list');

  const hasTagsViewPermission = permissions?.includes('individual.tags.view');
  const hasApiIdentityListPermission = permissions?.includes('apiIdentity.list');
  return (
    <Show
      {...props}
      title={
        <RecordTitle
          headline={(record: Individual, resource: 'individuals') =>
            buildName(resource, record) || '???'
          }
          record={showRecord}
          avatarSource='icon'
          resource='individuals'
          hasPathnameProfile={isProfileTab}
        />
      }
    >
      {showRecord && <UpdateContext data={{ solutionId: showRecord.solutionId }} />}
      <Layout history={props.history}>
        <Tab
          className={classes.tab}
          label='resources.individuals.tabs.accounts'
          disabled={!permissions?.includes('individual.account.view') || editMode !== 'VIEW'}
        >
          <Accounts
            resource='individuals'
            profileId={showRecord?.id || ''}
            permissions={permissions}
            basePath={basePath}
            solutionId={showRecord?.solutionId}
          />
          <ExternalSources
            profileId={showRecord?.id || ''}
            solutionId={showRecord?.solutionId}
            resource='individuals'
          />
          {hasCardIssuingSupport && (
            <Cards
              profileId={showRecord?.id || ''}
              solutionId={showRecord?.solutionId}
              resource='individuals'
            />
          )}
        </Tab>
        <Tab
          className={classes.tab}
          label='resources.individuals.tabs.profile'
          path='profile'
          disabled={!permissions?.includes('individual.view')}
        >
          <CheckPermissions permission='individual.view'>
            <DraftDiffProvider draft={showRecord}>
              <div className={classes.section}>
                <header className={classes.header}>
                  <H2 topSpacing={0} bottomSpacing={0}>
                    {translateLabel('profileInfo')}
                  </H2>
                  <EditAccountInfo
                    permissions={permissions}
                    record={showRecord}
                    resource={resource}
                  />
                </header>
                <div className={classes.container}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        source='firstName'
                        label={translateLabel('firstName')}
                        record={showRecord}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        source='lastName'
                        label={translateLabel('lastName')}
                        record={showRecord}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        source='addNames[0]'
                        label={translateLabel('middleName')}
                        record={showRecord}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <DiffHighlight source='birthDate'>
                        <TextFieldComponent label={translateLabel('birthDate')}>
                          {showRecord?.birthDate && time(showRecord?.birthDate).format('L')}
                        </TextFieldComponent>
                      </DiffHighlight>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <DiffHighlight source='gender'>
                        <TextFieldComponent label={translateLabel('gender._')}>
                          {showRecord?.gender && translateLabel(`gender.${showRecord.gender}`)}
                        </TextFieldComponent>
                      </DiffHighlight>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <DiffHighlight source='nationality'>
                        <TextField label={translateLabel('nationality')}>
                          <CountryField>{showRecord.nationality}</CountryField>
                        </TextField>
                      </DiffHighlight>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        source='placeOfBirth'
                        label={translateLabel('placeOfBirth')}
                        record={showRecord}
                      />
                    </Grid>
                    {showRecord?.referralCode && (
                      <Grid item xs={12} sm={6} md={4}>
                        <DiffHighlight source='referralCode'>
                          <TextField label={translateLabel('referralCode')}>
                            <CountryField>{showRecord.referralCode}</CountryField>
                          </TextField>
                        </DiffHighlight>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={4}>
                      <DiffHighlight source='branch'>
                        <BranchField source='branch' record={showRecord} />
                      </DiffHighlight>
                    </Grid>
                  </Grid>
                </div>
              </div>
              <div className={cx(classes.section, classes.topSpacing3)}>
                <header className={classes.header}>
                  <H2 topSpacing={0} bottomSpacing={0}>
                    {translateLabel('contacts')}
                  </H2>
                  {editMode === 'DRAFT' && (
                    <EditContacts
                      permissions={permissions}
                      resource='individuals'
                      record={showRecord}
                    />
                  )}
                </header>
                <div className={classes.container}>
                  {!!showRecord?.contacts && showRecord?.contacts.length > 0 ? (
                    <Grid container spacing={2}>
                      {showRecord?.contacts?.map((item, index) => {
                        const { id, channel } = item;
                        return (
                          <Grid xs={4} item key={index}>
                            <DiffScope source='contacts' index={index}>
                              <DiffHighlight group>
                                <TextFieldComponent
                                  label={translate(`components.ra.inputs.ChannelSelect.${channel}`)}
                                >
                                  {channel === 'PHONE' ? <PhoneField number={id} /> : id}
                                </TextFieldComponent>
                              </DiffHighlight>
                            </DiffScope>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    translateLabel('noContacts')
                  )}
                </div>
              </div>
              <div className={cx(classes.section, classes.topSpacing3)}>
                <header className={classes.header}>
                  <H2 topSpacing={0} bottomSpacing={0}>
                    {translateLabel('address._')}
                  </H2>
                  {editMode === 'DRAFT' && (
                    <EditAddress
                      permissions={permissions}
                      resource='individuals'
                      record={showRecord}
                    />
                  )}
                </header>
                <div className={classes.container}>
                  {showRecord?.address ? (
                    <Grid container>
                      <Grid xs={12} item>
                        <DiffScope source='address'>
                          <AddressTable address={showRecord.address} />
                        </DiffScope>
                      </Grid>
                    </Grid>
                  ) : (
                    translateLabel('noAddress')
                  )}
                </div>
              </div>
              <div className={cx(classes.section, classes.topSpacing3)}>
                <header className={classes.header}>
                  <H2 topSpacing={0} bottomSpacing={0}>
                    {translateLabel('documents._')}
                  </H2>
                  {editMode === 'DRAFT' && (
                    <AddOrEditDocument record={showRecord} permissions={permissions} />
                  )}
                </header>
                <div className={classes.container}>
                  {showRecord?.documents && showRecord?.documents.length > 0 ? (
                    <Grid className={classes.noneMaxWidth} container spacing={2}>
                      {showRecord?.documents?.map((document, index) => {
                        return (
                          <Grid item xs={6} xl={5} key={index}>
                            <DiffScope source='documents' item={document} index={index}>
                              <DocumentCard
                                resource='individuals'
                                record={showRecord}
                                solutionId={showRecord?.solutionId}
                                country={showRecord?.country}
                                documentFamily='INDIVIDUAL'
                                key={index}
                                index={index}
                                document={document}
                                fullWidth
                                permissions={permissions}
                                editDocument={(index, primary, handleClose) => (
                                  <AddOrEditDocument
                                    record={showRecord}
                                    edit
                                    primary={primary}
                                    index={index}
                                    closeParentDrawer={handleClose}
                                    permissions={permissions}
                                  />
                                )}
                              />
                            </DiffScope>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    translateLabel('noDocuments')
                  )}
                </div>
              </div>
              <div className={cx(classes.section, classes.topSpacing3)}>
                <header className={classes.header}>
                  <H2 topSpacing={0} bottomSpacing={0}>
                    {translateLabel('additionalProperties._')}
                  </H2>
                  {profileSchema?.schema &&
                    editMode === 'DRAFT' &&
                    Object.keys(profileSchema.schema.properties || {}).length > 0 && (
                      <EditAdditionalProperties
                        permissions={permissions}
                        record={showRecord!}
                        schema={profileSchema.schema}
                      />
                    )}
                </header>
                <div className={classes.container}>
                  {profileSchema?.schema ? (
                    Object.keys(profileSchema.schema.properties || {}).length > 0 ? (
                      <JsonSchemaView
                        data={showRecord}
                        schema={profileSchema?.schema}
                        displayMissingProperties
                        className={classes.noneMaxWidth}
                      />
                    ) : (
                      <>
                        {translateLabel('additionalProperties.empty')}
                        <br />
                        <br />
                      </>
                    )
                  ) : profileSchema?.error ? (
                    <Alert severity='warning' style={{ marginBottom: '1em' }}>
                      {translateLabel('additionalProperties.badSchema')}
                    </Alert>
                  ) : (
                    <Skeleton variant='text' width={200} height={24} />
                  )}
                </div>
              </div>
              {editMode === 'VIEW' && (
                <div className={cx(classes.section, classes.topSpacing3)}>
                  <header className={classes.header}>
                    <H2 topSpacing={0} bottomSpacing={0}>
                      {translateLabel('metrics')}
                    </H2>
                  </header>
                  <div className={classes.container}>
                    <Metrics id={removeDraftPostfix(showRecord?.id)} emptyMessage />
                  </div>
                </div>
              )}
            </DraftDiffProvider>
          </CheckPermissions>
        </Tab>
        <Tab
          className={classes.tab}
          label='resources.individuals.tabs.transactionHistory'
          path='transactions'
          disabled={!permissions.includes('individual.transaction.list') || editMode !== 'VIEW'}
        >
          <TransactionsHistory
            permissions={permissions}
            resource='individuals'
            record={showRecord}
          />
        </Tab>
        {hasBeneficiarySupport && (
          <Tab
            className={classes.tab}
            label='resources.individuals.tabs.beneficiaries'
            path='beneficiaries'
            disabled={editMode !== 'VIEW'}
          >
            <BeneficiariesList record={showRecord} resource={resource} permissions={permissions} />
          </Tab>
        )}
        {permissions?.includes('individual.view') &&
          permissions?.includes('individual.event.list') && (
            <Tab
              className={classes.tab}
              label='resources.individuals.tabs.eventLog'
              path='eventLog'
              disabled={editMode !== 'VIEW'}
            />
          )}
        {permissions?.includes('apiIdentity.list') && (
          <Tab
            className={classes.tab}
            label='resources.individuals.tabs.apiIdentity'
            path='apiIdentity'
            disabled={editMode !== 'VIEW'}
          >
            <ApiIdentityTab record={showRecord} />
          </Tab>
        )}
        {permissions?.includes('auditLog.list') && (
          <Tab
            className={classes.tab}
            label='resources.individuals.tabs.systemLog'
            path='systemLog'
            disabled={editMode !== 'VIEW'}
          />
        )}
        {permissions?.includes('individual.view') &&
          permissions?.includes('individual.event.list') && (
            <Tab
              className={classes.tab}
              label='resources.individuals.tabs.checks'
              path='checks'
              disabled={editMode !== 'VIEW'}
            >
              <EventLogKYC objectType='individual' object={showRecord} />
            </Tab>
          )}
        {permissions?.includes('individual.application.list') && (
          <Tab
            className={classes.tab}
            label='resources.individuals.tabs.applications'
            path='applications'
            disabled={editMode !== 'VIEW'}
          >
            <Applications target='individuals' />
          </Tab>
        )}
        {permissions?.includes('individual.view') && permissions?.includes('report.list') && (
          <Tab
            className={classes.tab}
            label='resources.individuals.tabs.actionReports'
            path='actionReports'
            disabled={editMode !== 'VIEW'}
          >
            <ActionReportsTab objectId={showRecord.id} objectType='INDIVIDUAL' />
          </Tab>
        )}
        {permissions?.includes('individual.view') &&
          permissions?.includes('individual.legalConsent.list') && (
            <Tab
              className={classes.tab}
              label='resources.individuals.tabs.legalConsents'
              path='legalConsents'
              disabled={editMode !== 'VIEW'}
            >
              <LegalConsentsTab />
            </Tab>
          )}
      </Layout>
      {currentPathname !== transactionsPathname && (
        <ShowController>
          {({ showRecord: record, ...rest }: any) => {
            return (
              <Grid container item xs direction='column' spacing={spacing}>
                <Grid item className={classes.rightContent}>
                  <Aside record={record} {...rest} />
                </Grid>
                {(isProfileTab || currentPathname === apiIdentityPathname) &&
                  editMode !== 'DRAFT' &&
                  editMode !== 'CHANGELOG' &&
                  hasApiIdentityListPermission && (
                    <Grid item className={classes.rightContent}>
                      <ApiIdentityAside record={record} resource='individuals' />
                    </Grid>
                  )}
                {(currentPathname === accountsPathname || isProfileTab) &&
                  (editMode === 'VIEW' || editMode === 'CHANGELOG') &&
                  hasTagsViewPermission && (
                    <Grid item className={classes.rightContent}>
                      <ProfileTags
                        profileId={record.id}
                        resource='individuals'
                        permissions={permissions}
                        record={record}
                      />
                    </Grid>
                  )}
                {currentPathname === accountsPathname && (
                  <Grid item className={classes.rightContent}>
                    <ProfileLimits
                      profileId={record.id}
                      resource='individuals'
                      solutionId={record?.solutionId}
                      permissions={permissions}
                    />
                  </Grid>
                )}
                {(currentPathname === accountsPathname || isProfileTab) &&
                  (editMode === 'VIEW' || editMode === 'CHANGELOG') && (
                    <AffiliatedObjects
                      id={record.id}
                      references={record?.references}
                      resource='individuals'
                    />
                  )}
                {(currentPathname === accountsPathname || isProfileTab) &&
                  editMode === 'VIEW' &&
                  hasExternalProfileLinksPermission && (
                    <Grid item className={classes.rightContent}>
                      <ExternalProfileLinks
                        profileId={record.id}
                        profileType='INDIVIDUAL'
                        permissions={permissions}
                      />
                    </Grid>
                  )}
                {isProfileTab && editMode === 'VIEW' && (
                  <Grid item className={cx(classes.rightContent, classes.fullWidthGrow)}>
                    <Notes referenceId={record.id} profileType='INDIVIDUAL' />
                  </Grid>
                )}
              </Grid>
            );
          }}
        </ShowController>
      )}
    </Show>
  );
};

type StylesPrpos = {
  spacing: number;
};

const useStyles = makeStyles<Theme, StylesPrpos>((theme) => ({
  tab: {
    minWidth: 80,
    flexGrow: 1,
    maxWidth: 200,
  },
  section: {
    '& header #section-actions': {
      opacity: 0.5,
      transition: `opacity ${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
    },
    '&:hover': {
      '& header #section-actions': {
        opacity: 1,
      },
    },
  },
  topSpacing3: {
    marginTop: ({ spacing }) => theme.spacing(spacing),
  },
  container: {
    paddingLeft: ({ spacing }) => theme.spacing(spacing),
    paddingRight: ({ spacing }) => theme.spacing(spacing),
  },
  header: {
    marginBottom: ({ spacing }) => theme.spacing(spacing),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  noneMaxWidth: {
    maxWidth: 'none',
  },
  rightContent: {
    marginLeft: ({ spacing }) => theme.spacing(spacing),
  },
}));

export default IndividualShow;
